import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Rocket, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { user, login, checked } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (checked && user && user.role === "admin") navigate("/admin");
  }, [checked, user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back!");
      navigate("/admin");
    } else {
      toast.error(res.error || "Login failed");
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen bg-gradient-to-br from-[#0b1121] via-[#1e1b4b] to-[#312e81] grid place-items-center p-6"
    >
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8" data-testid="admin-login-logo">
          <span className="grid place-items-center w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-fuchsia-600 shadow-lg shadow-purple-500/40">
            <Rocket className="w-5 h-5 text-white" />
          </span>
          <span className="font-display text-white text-xl font-extrabold">
            ROCKET<span className="text-purple-300">CAP</span>
          </span>
        </Link>

        <form
          onSubmit={submit}
          className="bg-white rounded-3xl shadow-2xl p-8"
          data-testid="admin-login-form"
        >
          <div className="flex items-center gap-2 mb-6">
            <span className="w-9 h-9 rounded-xl bg-purple-100 text-purple-600 grid place-items-center">
              <Lock className="w-4 h-4" />
            </span>
            <h1 className="font-display text-2xl font-black">Admin sign in</h1>
          </div>

          <label className="block mb-4">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">
              Email
            </span>
            <input
              data-testid="admin-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500"
            />
          </label>
          <label className="block mb-6">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">
              Password
            </span>
            <input
              data-testid="admin-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500"
            />
          </label>

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-btn"
            className="w-full rounded-xl bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-700 hover:to-fuchsia-700 disabled:opacity-50 text-white font-bold py-3.5 text-sm uppercase tracking-widest shadow-lg shadow-purple-500/30"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>

          <Link
            to="/"
            className="mt-6 block text-center text-xs text-slate-400 hover:text-slate-700 uppercase tracking-widest font-semibold"
          >
            ← Back to site
          </Link>
        </form>
      </div>
    </div>
  );
}
